import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Github, Upload, FileText } from 'lucide-react';
import { toast } from 'sonner';

const ImportSkillModal = ({ isOpen, onClose, initialMethod = null }) => {
  const [selectedMethod, setSelectedMethod] = useState(initialMethod);
  const [githubUrl, setGithubUrl] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleGithubImport = async () => {
    if (!githubUrl.trim()) {
      setError('Please enter a GitHub URL');
      return;
    }

    setIsImporting(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/skills/import/github`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ url: githubUrl })
      });

      // Check if body has already been read
      if (response.bodyUsed) {
        throw new Error('Response body already consumed');
      }

      // Clone response BEFORE reading it
      const clonedResponse = response.clone();
      
      let data;
      if (!response.ok) {
        // Read from clone for error
        data = await clonedResponse.json();
        throw new Error(data.detail || data.error || `Import failed (${response.status})`);
      }

      // Read from original for success
      data = await response.json();

      // Success - redirect
      toast.success('Skill imported successfully!');
      window.location.href = `/skills/${data.skill_slug}`;

    } catch (err) {
      setError(err.message || 'Failed to import from GitHub');
    } finally {
      setIsImporting(false);
    }
  };

  // Drag-and-drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = (file) => {
    const isZip = file.name.endsWith('.zip');
    const isMd = file.name.endsWith('.md');
    const isSkill = file.name.endsWith('.skill');

    if (!isZip && !isMd && !isSkill) {
      setError('Please upload a .zip, .md, or .skill file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File must be smaller than 10MB');
      return;
    }

    setIsImporting(true);
    setError(null);

    // Use XMLHttpRequest instead of fetch
    const formData = new FormData();
    formData.append('file', file);

    const xhr = new XMLHttpRequest();

    // Success handler
    xhr.onload = () => {
      setIsImporting(false);

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          console.log('Upload response:', data);
          
          toast.success(data.message || 'Skill imported successfully!');
          
          // Redirect to the skill page
          if (data.skill_slug) {
            console.log('Redirecting to:', `/skills/${data.skill_slug}`);
            window.location.href = `/skills/${data.skill_slug}`;
          } else if (data.skill_id) {
            console.log('Redirecting to:', `/skills/${data.skill_id}`);
            window.location.href = `/skills/${data.skill_id}`;
          } else {
            console.warn('No skill_slug or skill_id in response, redirecting to my-skills');
            window.location.href = '/my-skills';
          }
        } catch (err) {
          console.error('Parse error:', err);
          setError('Upload succeeded but response was invalid');
        }
      } else {
        // Error response
        try {
          const errorData = JSON.parse(xhr.responseText);
          setError(errorData.detail || errorData.error || `Upload failed (${xhr.status})`);
        } catch (err) {
          setError(`Upload failed (${xhr.status})`);
        }
      }
    };

    // Error handler
    xhr.onerror = () => {
      setIsImporting(false);
      setError('Network error - please check your connection');
    };

    // Abort handler
    xhr.onabort = () => {
      setIsImporting(false);
      setError('Upload cancelled');
    };

    // Send request
    const token = localStorage.getItem('token');
    xhr.open('POST', `${process.env.REACT_APP_BACKEND_URL}/api/skills/import/file`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 9999, top: 0, left: 0, right: 0, bottom: 0, position: 'fixed' }}>
      <div className="bg-white dark:bg-zinc-900 rounded-xl max-w-2xl w-full p-6 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <h2 className="text-2xl font-bold mb-2">Import Skill</h2>
        <p className="text-zinc-600 dark:text-zinc-400 mb-6">
          Add existing Claude Skills to your profile
        </p>

        {/* Method Selection or Forms */}
        {!selectedMethod ? (
          <div className="space-y-3">
            {/* GitHub Import */}
            <button
              onClick={() => setSelectedMethod('github')}
              className="w-full p-4 border-2 border-zinc-200 dark:border-zinc-700 rounded-lg hover:border-orange-500 dark:hover:border-orange-500 transition-colors text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                  <Github className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1 group-hover:text-orange-500 transition-colors">
                    Import from GitHub
                  </h3>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">
                    Import skills directly from GitHub repositories
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    Example: https://github.com/user/skill-name
                  </p>
                </div>
              </div>
            </button>

            {/* File Upload (Combined ZIP and Markdown) */}
            <button
              onClick={() => setSelectedMethod('upload')}
              className="w-full p-4 border-2 border-zinc-200 dark:border-zinc-700 rounded-lg hover:border-orange-500 dark:hover:border-orange-500 transition-colors text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                  <Upload className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1 group-hover:text-orange-500 transition-colors">
                    Upload File
                  </h3>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">
                    Upload a packaged skill.zip or SKILL.md file
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    Supports .zip packages and single .md files
                  </p>
                </div>
              </div>
            </button>
          </div>
        ) : (
          <>
            {/* Back button */}
            <button
              onClick={() => {
                setSelectedMethod(null);
                setError(null);
                setGithubUrl('');
              }}
              className="text-sm text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 mb-4"
            >
              ← Back to methods
            </button>

            {/* GitHub URL Input */}
            {selectedMethod === 'github' && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  GitHub Repository URL
                </label>
                <input
                  type="text"
                  value={githubUrl}
                  onChange={(e) => setGithubUrl(e.target.value)}
                  placeholder="https://github.com/username/skill-name"
                  className="w-full px-4 py-3 rounded-lg border-2 border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 mb-4 focus:border-orange-500 focus:outline-none transition-colors"
                  disabled={isImporting}
                />
                <p className="text-xs text-zinc-500 mb-4">
                  The repository must contain a SKILL.md file with proper YAML frontmatter
                </p>
                
                <button
                  onClick={handleGithubImport}
                  disabled={isImporting || !githubUrl.trim()}
                  className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isImporting ? 'Importing...' : 'Import from GitHub'}
                </button>
              </div>
            )}

            {/* File Upload with Drag & Drop */}
            {selectedMethod === 'upload' && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Upload Skill File
                </label>
                
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => !isImporting && fileInputRef.current?.click()}
                  className={`
                    relative border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer
                    ${isDragging 
                      ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/20 scale-[1.02]' 
                      : 'border-zinc-300 dark:border-zinc-700 hover:border-orange-400 dark:hover:border-orange-600'
                    }
                    ${isImporting ? 'opacity-50 pointer-events-none' : ''}
                  `}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".zip,.md"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file);
                    }}
                    className="hidden"
                  />
                  
                  {/* Upload Icon */}
                  <div className={`
                    w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center transition-all
                    ${isDragging 
                      ? 'bg-orange-500 scale-110' 
                      : 'bg-orange-100 dark:bg-orange-950/20'
                    }
                  `}>
                    <Upload className={`w-8 h-8 transition-all ${isDragging ? 'text-white' : 'text-orange-500'}`} />
                  </div>

                  {/* Text */}
                  <p className={`text-sm font-medium mb-1 transition-colors ${ 
                    isDragging ? 'text-orange-600 dark:text-orange-400' : 'text-zinc-900 dark:text-zinc-100'
                  }`}>
                    {isDragging ? 'Drop file here' : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-xs text-zinc-500">
                    .zip packages or .md files (max 10MB)
                  </p>

                  {/* Uploading State Overlay */}
                  {isImporting && (
                    <div className="absolute inset-0 bg-white/80 dark:bg-zinc-900/80 rounded-lg flex items-center justify-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                          Uploading...
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Info Box */}
                <div className="mt-4 p-4 bg-orange-50 dark:bg-dark-bg-tertiary rounded-lg border border-orange-200 dark:border-[#2e2e2d]">
                  <p className="text-sm text-orange-700 dark:text-gray-300">
                    <strong>ZIP packages</strong> should contain a SKILL.md file and optional bundled resources (scripts/, references/, assets/)
                  </p>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="mt-4 p-4 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-700 dark:text-red-300">
                  {error}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ImportSkillModal;
